import { Jobs, JobsWithSkillsGroup } from "@/interfaces/data_objects/jobs";
import { Candidates } from "@/interfaces/data_objects/candidates";
import { SearchJobObject } from "@/interfaces/shared/jobs/auto_complete_job_search";
import { Interview } from "@/interfaces/responses/interviews/interviews";
import { AssessmentsListingResponse } from "@/interfaces/responses/assessments/assessments";
import { SocialLinks } from "../onboarding/interfaces";
import { ZappyhireCandidate } from "@/interfaces/data_objects/user";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { SearchUserObject } from "@/interfaces/shared/auto_complete_user_search";
import { AppAssessmentNames } from "../common/interfaces";

export interface RecruiterState {
  loading: boolean;
  ai_assessment_listing: AssessmentAiListing[];
  app_ai_assessment_listing: AppAiAssessmentListing;
  fetch_app_ai_assessment: AppAssessments.AiAssessmentResults | null;
  jobs_posted_loading: boolean;
  poke_candidate_loading: boolean;
  view_candidates_dynamically: ViewCandidatesDynamically;
  add_standardised_job: JobsWithSkillsGroup | null;
  edited_Standardised_job: boolean;
  jobs_posted: {
    pagination: number;
    total: number;
    results: JobsWithCandidates[];
    date_filter: string[];
    searched_job_title: SearchAutocompleteJobPayload | null;
  };
  top_jobs_posted: {
    total: number;
    results: JobsWithCandidates[];
  };
  recommend_job_profiles: Jobs[];
  email_templates: EmailTemplates[];
  survey_questions: SurveyQuestions[];
  // When a recruiter wants to view a matching candidate, this payload will contain job_id & cv_id
  // Job id and cv id is required to view candidate profile
  candidate_view_payload: CandidateViewPayload | null;
  all_candidates: AllCandidates;
  post_job_limit_error: string;
  interviews: Interviews;
  view_candidates_pagination: ViewCandidatesPagination;
  assessment_listing: AssessmentListing;
  eng_assessment_listing: AppEngAssessmentListing;
  annotation_assessment_listing: AppAnnotationAssessmentListing;
  eng_assessment_listing_v2: AppEngAssessmentListing;
  annotation_assessment_listing_v2: AppAnnotationAssessmentListing;
  active_assessment: ActiveAssessment;
  inseption_stats: InceptionStats;
  overall_assessment_listing: AppOverAllAssessmentListing;
  cancelled_assessment_candidates: CancelledAssessmentCandidates;
  redo_assessment_candidates: RedoAssessmentCandidates;
  ai_session: {
    session_id: string | null;
    session_message: string;
    chat_messages: Array<{
      type: "user" | "ai";
      text: string;
      time: string;
      loading?: boolean;
    }>;
  };
  dashboard_counts: DashboardCounts;
  assessment_stats: AssessmentStats | null;
  available_applicants: AvailableApplicant[];
  selected_applicant: AvailableApplicant | null;
  selected_applicants?: AvailableApplicant[]; // multi-select support
  applicant_assessments: ApplicantAssessment[];
  applicant_assessment_details: ApplicantAssessmentDetailsResponse | null;
}

export interface AvailableApplicant {
  label: string;
  value: string;
}

export interface ApplicantAssessment {
  id: number;
  assessment: {
    id: number;
    name: string;
    type: string;
    difficulty: string;
    total_questions: number;
  };
  assigned_by: {
    id: number;
    name: string;
    email: string;
  };
  assigned_at: string;
  result: AppAssessments.AssessmentResult | null;
}

export interface ApplicantAssessmentsResponse {
  applicant: {
    id: number;
    name: string;
    email: string;
    type: string;
  };
  assessments: ApplicantAssessment[];
}

export interface ApplicantAssessmentDetailsResponse {
  applicant: {
    id: string;
    name: string;
    email: string;
    type: string;
  };
  assessment: {
    id: number;
    name: string;
    type: string;
    difficulty: string;
    is_active: boolean;
    total_questions: number;
  };
  questions: Array<{
    id: number;
    assessment_id: number;
    question_type: string;
    question_text: string;
    question_options: string[];
    question_time_allowed: number;
    is_question_enable: boolean;
    created_by: number;
    user_id: number;
    job_id?: number | null;
  }>;
  results: Array<{
    id: number;
    assessment_id: number;
    user_id: string;
    overall_loss: number;
    total_questions: number;
    current_question: number;
    status: string;
    result_data: unknown;
    urls: Record<string, unknown>;
    completed_at: string;
  }>;
}
export interface EmailTemplates {
  id: number;
  name: string;
  type: string;
  subject: string;
  body: string;
  created_at: Date;
  updated_at: Date;
}

export interface DashboardCounts {
  retaken_assessment_count: number;
  more_than_twice_retaken: number;
  signup_to_assessment_time: string;
  new_candidate_count: number;
  completed_all_assessments: number;
  eng_assessment_avg_time: string;
  anno_assessment_avg_time: string;
  eng_speaking_avg_time: string;
  eng_mcq_avg_time: string;
  eng_writing_avg_time: string;
  anno_part1_avg_time: string;
  anno_part2_avg_time: string;
  anno_part3_avg_time: string;
  anno_part4_avg_time: string;
  anno_part5_avg_time: string;
  anno_part6_avg_time: string;
  anno_part7_avg_time: string;
  anno_part8_avg_time: string;
  admin_approved_candidates: number;
  auto_approved_candidates: number;
  eng_assessment_candidates: number;
  anno_assessment_candidates: number;
  cancelled_assessments: number;
  passed_assessments: number;
  failed_assessments: number;
  avg_time_ans: string;
  registered_users: number;
}

export interface SurveyQuestions {
  id: number;
  type: string;
  text: string;
  created_at: Date;
}
export interface AddSurveyQuestionPayload {
  type: string;
  text: string;
  text_arabic: string;
}
export interface SubmitSurveyQuestionsPayload {
  responses: SubmitSurveyQuestions[];
}
export interface SubmitSurveyQuestions {
  question_id: number;
  response: string;
}
export interface InceptionStats {
  users: string;
  englishAssessment: string;
  annotationAssessment: string;
  allUsers: string;
}
export interface AssessmentListing {
  fetch_loading: boolean;
  pagination: number;
  selected_dates: string[];
  data: AssessmentsListingResponse.Root;
}
export interface ViewCandidatesPagination {
  pagination: number;
}
export interface ViewCandidatesDynamically {
  active: boolean;
  loading: boolean;
  job_id: number | null;
  job_title: string;
  skill: string;
  user_ids: [];
}
export interface JobsWithCandidates extends Jobs {
  matching_candidates: SearchedCandidate[];
  candidates_loading: boolean;
}

export interface PostJobPayload {
  job_title: string;
  job_description: string;
  job_industry: string;
  job_type: string;
  job_experience: string;
  skills_list: string;
  is_standardize: boolean;
  edited: boolean;
}

export interface GetJobsPostedPayload {
  page?: number;
  limit_per_page?: number;
  dashboard?: boolean;
  is_standardize?: boolean;
  job_title?: string;
  date_filter?: string[];
}
export interface SetJobsMutationPayload {
  total: number;
  results: JobsWithCandidates[];
}

export interface MatchingCandidatesByIdPayload {
  job_id: number;
  n?: number;
  dashboard?: boolean;
  skill_name?: string;
  page?: number;
  limit_per_page?: number;
  user_ids?: number[];
}

export interface UpdateMatchingCandidatePayload {
  job_id: number;
  candidates: null | Candidates.Candidates[];
}

export interface UpdateJobPayload {
  job_id: number;
  job_title: string;
  job_description: string;
  job_sector: string;
  job_experience: string;
  job_employment_type: string;
  job_skills_list: string;
}

export interface UpdateApprovalCriteriaPayload {
  auto_approval: boolean;
  laptop_availability: boolean;
  wifi_availability: boolean;
  full_time_availability: boolean;
  location: string;
}
export interface AnswerPayload {
  auto_approval: boolean;
  approval_criteria: boolean;
  laptop_availability: boolean;
  wifi_availability: boolean;
  full_time_availability: boolean;
  location: string;
}
export interface UpdateEmailTemplatePayload {
  subject: string;
  body: string;
  template_id: number;
}

export interface FetchAssessmentAiListingPayload {
  user_id: number;
  page?: number;
  limit?: number;
}

export interface FetchAssessmentAiQuestionsPayload {
  page?: number;
  limit?: number;
}
export interface EmailTemplate {
  name: string;
  subject: string;
  type: string;
  body: string;
  html: string;
  created_at: string;
  updated_at: string;
}
export interface CandidateViewPayload {
  job_id: number;
  cv_id: number;
  upload_response_id: string;
}

export interface MembersJobsStatistics {
  filter: string;
  start_date: string;
  end_date: string;
}

export interface UpdateFavoriteCandidatePayload {
  is_favorite: boolean;
  candidate_id: number;
  job_id: number;
}

export interface AllCandidates {
  total: number;
  results: Candidates.Candidates[];
  filtered_count: number;
}

// Interface for poke candidate request payload
export interface PokeCandidatePayload {
  job_id: number; // Job jid
  candidate_id: number; // User pk
  type?: string;
}

// Interface for saving updated job description
export interface SaveUpdatedJobDescriptionPayload {
  job_id: number;
  description: string;
  sector: string;
}

// For start interview API POST call payload
export interface StartInterviewPayload {
  candidate_id: number;
  interview_id: number;
}

// For resume interview API POST call payload
export interface ResumeInterviewPayload {
  interview_id: number;
  answer?: string;
}
// For storing interview variables
export interface Interviews {
  search_job_loading: boolean;
  searched_job: SearchJobObject | null;
  pagination: number;
  interview_listing: Map<number, InterviewListingResult>;
  selected_candidate: InterviewListingCandidates[];
}

export interface InterviewListing {
  [id: string]: InterviewListingResult;
}

export interface InterviewListingResult {
  id: number;
  title: number;
  date: string;
  candidates: InterviewListingCandidates[];
  zappy_job_id?: number;
}

export interface InterviewListingCandidates {
  avatar_uri: string;
  first_name: string;
  interview_status: string;
  last_name: string;
  id: number;
  class: string;
  readable_status: string;
  interview_by: number;
  interview_id: number;
  candidate_by: number;
  job_id: number;
  content?: string;
  candidate_id?: number;
  zappy_job_id?: number;
}
export interface InterviewListingPayload {
  page?: number;
  limit?: number;
  interviewed_by?: number;
  job_id?: number;
  candidate_id?: number;
  interview_id?: number;
}

export interface UpdateInterviewStatusPayload {
  interview_id: number;
  action: Interview.Status;
  recording?: File;
  filename?: string;
}

export interface InterviewHistoryData {
  [key: number]: string;
}

export interface IndividualInterviewHistory {
  candidate_interview_history: string;
  name: string;
  avatar?: string;
  candidate_id?: number;
  zappy_job_id?: number;
  job_id: number;
}

export interface InterviewReport {
  [key: string]: InterviewReportDetails;
}
export interface InterviewReportDetails {
  rating: number;
  reason: string;
  color: string;
  bg_color: string;
  icon: string;
  key: string;
}

export interface FetchAssessmentListingPayload {
  page: number;
  limit: number;
  selected_searched_user: number[];
  date_picker_field: string[] | null;
}

export interface AssessmentAiListing {
  id: number;
  name: string;
  difficulty: string;
  assessment_type: string;
  assessment_name: number;
  is_active: boolean;
  created_at: Date;
  allowed_time: number;
  questions?: AssessmentQuestion[];
  total_questions: number;
}

export interface FetchAssessmentAiListingPayload {
  user_id: number;
}

export interface SearchAutocompleteJobPayload {
  job_id: number;
  text: string;
}

export interface SearchedCandidate {
  job_id?: number;
  score?: number;
  candidate_id: number;
  upload_response_id: string;
  upload_response: {
    profile?: SearchedCandidateProfile;
  };
  avatar_uri: string;
  is_favorite?: boolean;
  social_links: SocialLinks[];
  availability: string[];
  status: string;
  is_account_confirmed?: boolean;
  last_name?: string;
  first_name?: string;
  eid?: number;
  email?: string;
  entity_location?: string;
}
export interface CancelledAssessmentCandidate {
  id: number;
  upload_response_id: string;
  upload_response: {
    profile?: SearchedCandidateProfile;
  };
  avatar_uri: string;
  is_favorite?: boolean;
  social_links: SocialLinks[];
  availability: string[];
  status: string;
  is_account_confirmed?: boolean;
  last_name?: string;
  first_name?: string;
  eid?: number;
  email?: string;
  entity_location?: string;
}
export interface SearchedCandidateProfile {
  personal_details: {
    name: {
      first_name: string;
      middle_name: string;
      last_name: string;
    };
  };
  work_history_detailed?: {
    [key: string]: {
      job_title: string;
    };
  };
  total_experience?: string;
}

export interface SearchCandidatesPayload {
  job_ids?: number[];
  n?: number;
  dashboard?: boolean;
  skill_name?: string;
  page?: number;
  limit_per_page?: number;
  user_ids?: number[];
  registered?: boolean;
  approved?: boolean;
}

export interface SearchCandidatesAccumulator {
  [key: number]: SearchedCandidate[];
}

export interface UpdateMatchingCandiadteMutationPayload {
  candidates_job_based: SearchCandidatesAccumulator;
}

export interface ZappyhireCreateCandidatePayload {
  job_id: number;
  payload: ZappyhireCandidate;
  candidate_id: number;
}

export interface ZapphirePostJobResponse {
  status: number;
  errors: string;
  results: {
    job_id?: number;
  };
}

export interface ZapphireCreateCandidateResponse {
  results?: {
    candidate_id?: number;
  };
}

export interface UpdateZappyInterviewListingObjectPayload {
  job_id: number;
  zappy_job_id: number;
}

export interface AppEngAssessmentListing {
  page: number;
  limit: number;
  total: number;
  results: AppAssessments.AssessmentResults[];
  selected_dates: DateFilterPayload;
  selected_candidates: SearchUserObject[];
  score_filter?: string;
  status_filter?: string;
  sort_by?: string;
  sort_order?: string;
  user?: AppAssessments.AssessmentUserObject;
}

export interface AppAiAssessmentListing {
  page: number;
  limit: number;
  total: number;
  results: AppAssessments.AiAssessmentResults[];
  selected_dates: DateFilterPayload;
  selected_candidates: SearchUserObject[];
  score_filter?: string;
  status_filter?: string;
  user?: AppAssessments.AssessmentUserObject;
  sort_by?: string;
  sort_order?: string;
}
export interface AppAnnotationAssessmentListing {
  page: number;
  limit: number;
  total: number;
  results: AppAssessments.AnnotationAssessmentResults[];
  selected_dates: DateFilterPayload;
  selected_candidates: SearchUserObject[];
  score_filter?: string;
  status_filter?: string;
  sort_by?: string;
  sort_order?: string;
  user?: AppAssessments.AssessmentUserObject;
}
export interface AppAssessmentListingPayload {
  page: number;
  limit: number;
  assessment_id: number;
  selected_dates: DateFilterPayload;
  selected_candidates: SearchUserObject[];
  version?: string;
  score_filter?: string;
  status_filter?: string;
  sort_by?: string;
  sort_order?: string;
  fetch_all_annotations?: boolean;
}
export interface FetchAppAiAssessmentPayload {
  assessment_id: number;
  user_id: number;
}

export interface FetchAssessmentResult {
  result_id: number;
  assessment_id: number;
}

export interface ActiveAssessment {
  assessment_id: number | null;
  assessment_type: string;
}

export interface DateFilterPayload {
  date_picker_menu: boolean;
  date_range_value: string;
  date_filter: string[];
}

export interface DeleteAssessmentPayload {
  assessment_id: number;
  user_id: number;
}
export interface ResetAssessmentPayload {
  assessment_id: number;
  user_id: number;
}

export interface CreateAssessmentPayload {
  assessment_name: string;
  assessment_type: string;
}

export interface QuestionOption {
  [key: string]: any;
}

export interface AssessmentQuestion {
  id: number;
  question_text: string;
  question_type: string;
  is_question_enable: boolean;
  created_by: number;
  question_options: QuestionOption;
  question_time_allowed: number;
  user_id?: number;
  job_id?: number;
  assessment_id?: number;
  correct_answer?: string;
  answer?: string;
}

export interface CreateAssessmentQuestionsPayload {
  questions: Omit<AssessmentQuestion, "id" | "correct_answer">[];
}

export interface DeleteCancelledAssessmentPayload {
  user_ids: number[];
  assessment_version: number;
  is_reset_all: boolean;
  is_redo?: boolean;
}
export enum CompanySettingTypes {
  ASSESSMENT_SETTING = "assessment_setting"
}

export interface UpdateCompanySettingPayload {
  type: CompanySettingTypes;
  current_assessment_type: AppAssessments.CurrentAssessmentType;
  current_assessment: AppAssessmentNames;
}
export enum AssessmentVersions {
  VERSION_1 = "v1",
  VERSION_2 = "v2",
  VERSION_3 = "v3"
}

export interface AppOverAllAssessmentListing {
  page: number;
  limit: number;
  total: number;
  results: AppOverAllResult[];
  selected_dates: DateFilterPayload;
  selected_candidates: SearchUserObject[];
  score_filter: string;
  eid_name_filter: string;
  average_score_filter: number[];
  english_assessment_filter: number[];
  annotation_assessment_filter: number[];
  assessment_versions: AssessmentVersions[];
  download_report?: boolean;
}

export interface AppOverAllResult {
  user_id: number | null;
  english_assessment: AppAssessments.AssessmentResult | null;
  annotation_assessment: AppAssessments.AnnotationAssessmentResult | null;
  user: AppAssessments.AssessmentUserObject | null;
  average_score: string;
  date: string;
  annotation_assessment_id: number | null;
  english_assessment_id: number | null;
}

export interface CancelledAssessmentCandidates {
  page: number;
  limit: number;
  total: number;
  results: CancelledAssessmentCandidatesResults[];
  user_ids: number[];
  assessment_version: string;
}
export interface RedoAssessmentCandidates {
  page: number;
  limit: number;
  total: number;
  results: RedoAssessmentCandidatesResults[];
  user_ids: number[];
  assessment_version: string;
}
export interface CancelledAssessmentCandidatesResults {
  user_id: number | null;
  assessment_status: string;
  assessment_result_id: number | null;
  user: AppAssessments.AssessmentUserObject | null;
}
export interface RedoAssessmentCandidatesResults {
  user_id: number | null;
  assessment_status: string;
  assessment_result_id: number | null;
  user: AppAssessments.AssessmentUserObject | null;
}
export interface FetchCancelledAssessmentCandidatesPayload {
  page: number;
  limit: number;
  user_ids: number[];
  assessment_version: string;
}
export interface FetchRedoAssessmentCandidatesPayload {
  page: number;
  limit: number;
  user_ids: number[];
  assessment_version: string;
}
export interface RedoAssessmentRequestPayload {
  assessment_id: number;
}

export interface AssessmentStats {
  total_assessments: {
    count: number;
    change_percentage: number;
    change_direction: "up" | "down";
  };
  total_users: {
    count: number;
    change_percentage: number;
    change_direction: "up" | "down";
  };
  assessments_taken: {
    count: number;
    change_percentage: number;
    change_direction: "up" | "down";
  };
  average_score: {
    percentage: number;
    change_percentage: number;
    change_direction: "up" | "down";
  };
}
